import React, { useState, useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { NavLink, useNavigate, useParams } from "react-router-dom"
import { showSnackBar } from "../../../components/Snackbar/Snackbar"
import ContainerPage from "../../../components/ContainerPage/ContainerPage"
import PageTitle from "../../../components/PageTitle/PageTitle"
import BankAccountContainer from "./BankAccountContainer"
import Button from "../../../components/Button/Button"
import { areFieldsNotEmpty } from "../../../helpers/helper"
import Input from "../../../components/Input/Input"
import Dropdown from "../../../components/Dropdown/Dropdown"
import { fetchStates } from "../../../features/states/states"
import { clearNumber } from "../../../utils/payment"
import CreditCardContainer from "./CreditCardContainer"
import {
  updateBillingAddress,
  updateCardById,
  updateCardSecretsById,
  updateBankInfo,
  updateBankSecrets
} from "../../../features/bankAccountLink/bankAccountLink"
import { StorageGetItem, USER } from "../../../lib/storage"
import { useIsSupervisor } from "../../../hooks/user"
import { selectFeatureFlag } from "../../../features/featureFlags/selectors"
import { EDIT_BILLING_WAIT_FOR_SAVE } from "../../../config/featureFlagKeys"
import { useUiKit } from "../../../components/UiKit/UiKitContext"

const EditBilling = ({ viewOnly = false, successNavTo = false, cancelNavTo = false }) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const isSupervisor = useIsSupervisor()
  const waitForSave = useSelector(selectFeatureFlag(EDIT_BILLING_WAIT_FOR_SAVE))
  const { button: kitButton } = useUiKit()
  const [saving, setSaving] = useState(false)
  const { billingId, billingType } = useParams()

  useEffect(() => {
    if (isSupervisor && billingType === 'bank') {
      showSnackBar("error", "Access denied: Bank account details are restricted")
      navigate(-1)
    }
  }, [isSupervisor, billingType, navigate])

  const [reqError, setReqError] = useState('')
  const [billingAddress, setBillingAddress] = useState({
    address: '',
    address2: '',
    city: '',
    state: '',
    zipcode: ''
  })
  const [cardUpdateData, setCardUpdateData] = useState({
    provider: '',
    nameOnCard: '',
    expiryMonth: '',
    expiryDate: ''
  })
  const [cardSecretsUpdateData, setCardSecretsUpdateData] = useState({
    cardNumber: '',
    cvv: ''
  })
  const [bankAccountData, setBankAccountData] = useState({
    bankName: '',
    nameOnAccount: ''
  })
  const [bankAccountSecrets, setBankAccountSecrets] = useState({
    routingNumber: '',
    accountNumber: ''
  })
  const [states, setStates] = useState([])
  const [userRole, setUserRole] = useState('')

  useEffect(() => {
    StorageGetItem(USER).then(result => {
      setUserRole(result?.role)
    })
  }, [])

  useEffect(() => {
    if (reqError) {
      showSnackBar("error", reqError)
      setReqError('')
    }
  }, [reqError])

  const getStates = async () => {
    try {
      const response = await dispatch(fetchStates({ searchQuery: '' }))
      setStates(response?.payload?.data)
    } catch (error) {
      showSnackBar("error", error)
    }
  }

  // The handlers keep the form as typed: Save closes the screen on success, and on a refusal the
  // rep fixes the field and saves again
  const handleEditBillingAddress = async () => {
    const response = await dispatch(updateBillingAddress({
      billingId,
      data: billingAddress
    }))
    if (response.error) {
      setReqError(response.payload)
      return false
    } else {
      showSnackBar("success", "Billing address updated")
      return true
    }
  }

  const handleEditCreditCardInfo = async () => {
    const response = await dispatch(updateCardById({
      billingId,
      data: cardUpdateData
    }))
    if (response.error) {
      setReqError(response.payload)
      return false
    } else {
      showSnackBar("success", "Credit card info updated")
      return true
    }
  }

  const handleEditCreditCardSecrets = async () => {
    const response = await dispatch(updateCardSecretsById({
      billingId,
      data: cardSecretsUpdateData
    }))
    if (response.error) {
      setReqError(response.payload)
      return false
    } else {
      showSnackBar("success", "Credit card secrets updated")
      return true
    }
  }

  const handleEditBankInfo = async () => {
    const response = await dispatch(updateBankInfo({
      billingId,
      data: bankAccountData
    }))
    if (response.error) {
      setReqError(response.payload)
      return false
    } else {
      showSnackBar("success", "Bank info updated")
      return true
    }
  }

  const handleEditBankSecrets = async () => {
    const response = await dispatch(updateBankSecrets({
      billingId,
      data: bankAccountSecrets
    }))
    if (response.error) {
      setReqError(response.payload)
      return false
    } else {
      showSnackBar("success", "Bank secrets updated")
      return true
    }
  }

  const handleInputChange = ({ name, value }) => {
    if (!viewOnly) {
      if (name === 'zipcode') {
        setBillingAddress(prev => ({ ...prev, [name]: clearNumber(value) }))
      } else {
        setBillingAddress(prev => ({ ...prev, [name]: value }))
      }
    }
  }

  useEffect(() => {
    getStates()
  }, [])

  // Early return for supervisor trying to access bank account
  if (isSupervisor && billingType === 'bank') {
    return null
  }

  const isDataFilled = (data) => {
    return Object.values(data).some(value => value?.trim?.() !== '')
  }

  // Resolves to true when every update sent was accepted
  const submitUpdates = async () => {
    const updates = []
    if ((userRole === 'customer' || isSupervisor) && !viewOnly) {
      updates.push(handleEditBillingAddress())
      if (billingType === 'card') {
        if (isDataFilled(cardUpdateData)) {
          updates.push(handleEditCreditCardInfo())
        }
        if (isDataFilled(cardSecretsUpdateData)) {
          updates.push(handleEditCreditCardSecrets())
        }
      } else if (billingType === 'bank') {
        if (isDataFilled(bankAccountData)) {
          updates.push(handleEditBankInfo())
        }
        if (isDataFilled(bankAccountSecrets)) {
          updates.push(handleEditBankSecrets())
        }
      }
    }
    const results = await Promise.all(updates)
    return results.every(Boolean)
  }

  return (
    <ContainerPage>
      <div className="flex justify-between items-center">
        <PageTitle title="Edit Billing Info" />
      </div>
      <div className="flex w-full items-center justify-center">
        <div className="w-full max-w-[850px] bg-surface border border-divider rounded-2xl shadow-card p-12">
          {billingType === 'bank' &&
            <BankAccountContainer
              billingId={billingId}
              setBillingAddress={setBillingAddress}
              setBankAccountData={setBankAccountData}
              setBankAccountSecrets={setBankAccountSecrets}
              viewOnly={viewOnly}
            />}
          {billingType === 'card' &&
            <CreditCardContainer
              viewOnly={viewOnly}
              billingId={billingId}
              setBillingAddress={setBillingAddress}
              setCardSecretsUpdateData={setCardSecretsUpdateData}
              setCardUpdateData={setCardUpdateData}
            />}
          <div className="my-4 font-bold">Billing Address</div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-6 h-max">
            <Input
              width="w-full"
              placeholder="Address"
              label="Address"
              value={billingAddress?.address}
              disabled={viewOnly}
              onChange={(e) => handleInputChange({ name: 'address', value: e.target.value })}
            />
            <Input
              width="w-full"
              placeholder="Address 2"
              label="Address 2"
              value={billingAddress?.address2}
              disabled={viewOnly}
              onChange={(e) => handleInputChange({ name: 'address2', value: e.target.value })}
            />
            <Input
              width="w-full"
              placeholder="City"
              label="City"
              value={billingAddress?.city}
              disabled={viewOnly}
              onChange={(e) => handleInputChange({ name: 'city', value: e.target.value })}
            />
            {viewOnly ? (
              <Input
                width="w-full"
                type="text"
                placeholder="State"
                label="State"
                value={billingAddress?.state}
                disabled={viewOnly}
              />
            ) : (
              <Dropdown
                menuItems={states?.map((state) => ({
                  label: `${state.shortName} - ${state.fullName}`,
                  value: state.shortName
                }))}
                label="State"
                placeholder="Enter State"
                value={billingAddress?.state}
                type="text"
                name="state"
                className="form-control w-full"
                required={true}
                setValue={(e) => handleInputChange({ name: 'state', value: e })}
              />
            )}
            <Input
              width="w-full"
              type="text"
              placeholder="Zip"
              label="Zip"
              value={billingAddress?.zipcode}
              onChange={(e) => handleInputChange({ name: 'zipcode', value: e.target.value })}
              disabled={viewOnly}
            />
          </div>
          <div className="mt-6 flex justify-end space-x-4">
            <NavLink to={cancelNavTo || "#"} onClick={(e) => {
              e.preventDefault()
              navigate(-1)
            }}>
              <Button
                onClick={() => {
                }}
                classes="mr-4"
                type="outlined"
                size="lg"
                width="min-w-[156px]"
              >
                Close
              </Button>
            </NavLink>
            {!viewOnly && (
              <NavLink to={successNavTo || "#"} onClick={async (e) => {
                e.preventDefault()
                if (!waitForSave) {
                  submitUpdates()
                  navigate(-1)
                  return
                }
                if (kitButton) {
                  if (saving) return
                  setSaving(true)
                  let saved = false
                  try {
                    saved = await submitUpdates()
                  } finally {
                    setSaving(false)
                  }
                  if (saved) navigate(-1)
                  return
                }
                // Stay open on a refusal so the rep sees it (e.g. a bad routing number)
                if (await submitUpdates()) {
                  navigate(-1)
                }
              }}>
                <Button
                  onClick={() => {
                  }}
                  size="lg"
                  width="min-w-[156px]"
                  disabled={!areFieldsNotEmpty([billingAddress?.address, billingAddress?.state, billingAddress?.city, `${billingAddress?.zipcode}`])}
                  loading={saving}
                  classes="rounded-lg"
                >
                  Save
                </Button>
              </NavLink>
            )}
          </div>
        </div>
      </div>
    </ContainerPage>
  )
}

export default EditBilling